import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTypingEffect } from '@/hooks/useTypingEffect';

interface MessageDisplayProps {
  content: string;
  isTyping?: boolean;
  messageId?: string;
  onTypingComplete?: (messageId: string) => void;
}

export const MessageDisplay: React.FC<MessageDisplayProps> = ({ 
  content, 
  isTyping = false, 
  messageId,
  onTypingComplete 
}) => {
  const navigate = useNavigate();
  
  // Use callback ref to ensure stable function reference
  const scrollCallback = React.useCallback(() => {
    setTimeout(() => {
      const messagesContainer = document.querySelector('[data-radix-scroll-area-viewport]');
      if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    }, 0);
  }, []);

  const { displayedText, isTyping: isCurrentlyTyping } = useTypingEffect(
    isTyping ? content : '', 
    30, 
    scrollCallback
  );

  // Call onTypingComplete when typing effect finishes
  React.useEffect(() => {
    if (isTyping && !isCurrentlyTyping && displayedText === content && messageId && onTypingComplete) {
      onTypingComplete(messageId);
    }
  }, [isTyping, isCurrentlyTyping, displayedText, content, messageId, onTypingComplete]);
  
  // Use typing effect only for new messages, otherwise show full content
  const textToShow = isTyping ? displayedText : content;
  
  return (
    <div className="text-sm whitespace-pre-wrap">
      {textToShow.split(/(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*)/).map((part, index) => {
        const linkMatch = part.match(/\[([^\]]+)\]\(([^)]+)\)/);
        const boldMatch = part.match(/\*\*([^*]+)\*\*/);
        
        if (linkMatch) {
          const [, text, route] = linkMatch;
          return (
            <button
              key={index}
              onClick={() => navigate(route)}
              className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none p-0"
            >
              {text}
            </button>
          );
        }
        
        if (boldMatch) {
          const [, text] = boldMatch;
          return <strong key={index}>{text}</strong>;
        }
        
        return part;
      })}
      {isTyping && displayedText && <span className="animate-pulse">|</span>}
    </div>
  );
};